import extend from 'object-assign';
import q from 'component-query';
import cookie from 'cookie-cutter';
import ua from 'ua-parser-js';
import { customEvents } from '../helpers/constants';

// IE < 11 doesn't support navigator language property.
/* global navigator */
const userLangAttribute =
  navigator.language || navigator.userLanguage || navigator.browserLanguage;
const userLang = userLangAttribute.slice(-2) || 'us';
const agent = ua(navigator.userAgent);

class AppBanner {
  constructor(config) {
    this.config = extend(
      {},
      {
        daysHidden: 15,
        daysReminder: 90,
        appStoreLanguage: userLang, // Language code for App Store
        button: 'OPEN', // Text for the install button
        score: {
          ios: null,
          android: null
        },
        force: '' // put platform type ('ios', 'android', etc.) here for emulation
      },
      config || {}
    );

    if (this.config.force) {
      this.type = this.config.force;
    } else if (agent.os.name === 'iOS') {
      this.type = 'ios';
    } else if (agent.os.name === 'Android') {
      this.type = 'android';
    }

    // Don't show banner on ANY of the following conditions:
    // - device os is not supported,
    // - running on standalone mode
    // - user dismissed banner
    if (!this.type) {
      return;
    }

    this.appId = this.parseAppId();

    const runningStandAlone = navigator.standalone;
    const userDismissed = cookie.get(`${this.appId}-app-banner-closed`);
    const userInstalled = cookie.get(`${this.appId}-app-banner-installed`);

    if (runningStandAlone || userDismissed || userInstalled) {
      return;
    }

    // - If we dont have app id in meta, dont display the banner
    if (!this.appId) {
      return;
    }

    this.create();
    this.show();
  }

  parseAppId() {
    const appMeta = { ios: 'apple-itunes-app', android: 'google-play-app' };
    const meta = q(`meta[name="${appMeta[this.type]}"]`);

    return meta
      ? /app-id=([^\s,]+)/.exec(meta.getAttribute('content'))[1]
      : null;
  }

  getScore(score) {
    if (!score) {
      return;
    }

    const rounded = Math.floor(score * 2) / 2;

    let html = '<div class="app-banner__rating">';

    for (let i = 1; i <= 5; i++) {
      if (i <= rounded) {
        html += `
          <svg viewBox="0 0 18 18" class="app-banner__rating-star app-banner__rating-star--full">
            <path d="M9.5 14.25l-5.584 2.936 1.066-6.218L.465 6.564l6.243-.907L9.5 0l2.792 5.657 6.243.907-4.517 4.404 1.066 6.218" />
          </svg>
        `;
      } else if (i - rounded === 0.5) {
        html += `
          <svg viewBox="0 0 18 18" class="app-banner__rating-star app-banner__rating-star--half">
            <path d="M9.5 14.25l-5.584 2.936 1.066-6.218L.465 6.564l6.243-.907L9.5 0l2.792 5.657 6.243.907-4.517 4.404 1.066 6.218" />
            <path d="M9.5 14.25l-5.584 2.936 1.066-6.218L.465 6.564l6.243-.907L9.5 0l2.792" />
          </svg>
        `;
      } else {
        html += `
          <svg viewBox="0 0 18 18" class="app-banner__rating-star app-banner__rating-star--empty">
            <path d="M9.5 14.25l-5.584 2.936 1.066-6.218L.465 6.564l6.243-.907L9.5 0l2.792 5.657 6.243.907-4.517 4.404 1.066 6.218" />
          </svg>
        `;
      }
    }

    html += '</div>';

    return html;
  }

  create() {
    const storeLink = {
      ios: `https://itunes.apple.com/${this.config.appStoreLanguage}/app/id${this.appId}?mt=8`,
      android: `http://play.google.com/store/apps/details?id=${this.appId}`
    };
    const iconRels = {
      ios: ['apple-touch-icon-precomposed', 'apple-touch-icon'],
      android: [
        'android-touch-icon',
        'apple-touch-icon-precomposed',
        'apple-touch-icon'
      ]
    };

    const link = storeLink[this.type];
    let icon;

    for (let i = 0; i < iconRels[this.type].length; i++) {
      const rel = q(`link[rel="${iconRels[this.type][i]}"]`);

      if (rel) {
        icon = rel.getAttribute('href');
        break;
      }
    }

    const sb = document.createElement('div');
    sb.className = 'app-banner';
    sb.innerHTML = `
      <div class="app-banner__container">
        <button class="app-banner__close" aria-label="Close"></button>
        <span class="app-banner__icon" style="background-image: url(${icon})"></span>
        <div class="app-banner__info">
          <p class="app-banner__title">${this.config.title}</p>
          <p class="app-banner__description">${this.config.description}</p>
          ${this.getScore(this.config.score[this.type])}
        </div>
        <a href="${link}" class="app-banner__button">
          ${this.config.button}
        </a>
      </div>
    `;

    // there isn’t neccessary a body
    document.body.appendChild(sb);

    q('.app-banner__button', sb).addEventListener(
      'click',
      this.install.bind(this),
      false
    );
    q('.app-banner__close', sb).addEventListener(
      'click',
      this.close.bind(this),
      false
    );
  }

  show() {
    document.documentElement.classList.add('app-banner-show');
  }

  hide() {
    this.updateLeftBorder();
    document.documentElement.classList.remove('app-banner-show');
  }

  updateLeftBorder() {
    const resizeEvent = new CustomEvent(customEvents.leftBorderResizeTrigger);
    window.dispatchEvent(resizeEvent);
  }

  install() {
    this.hide();
    cookie.set(`${this.appId}-app-banner-closed`, 'true', {
      path: '/',
      expires: new Date(
        Number(new Date()) + this.options.daysReminder * 1000 * 60 * 60 * 24
      )
    });
  }

  close() {
    this.hide();
    cookie.set(`${this.appId}-app-banner-closed`, 'true', {
      path: '/',
      expires: new Date(
        Number(new Date()) + this.options.daysHidden * 1000 * 60 * 60 * 24
      )
    });
  }
}

export default AppBanner;
